import { useState } from "react";
import { Row, Col } from "react-bootstrap";
import CalcShell from "../../components/CalcShell";
import CalcField from "../../components/CalcField";
import { formatINR } from "../../utils/format";

const meta = { slug: "retirement", emoji: "🌴", title: "Retirement Calculator", desc: "How much you need to retire comfortably." };

function RetirementCalculator() {
  const [currentAge, setCurrentAge] = useState(30);
  const [retireAge, setRetireAge] = useState(60);
  const [monthlyExpense, setMonthlyExpense] = useState(40000);
  const [inflation, setInflation] = useState(6);
  const [returns, setReturns] = useState(12);

  const yearsToRetire = Math.max(retireAge - currentAge, 0);
  // Expenses at retirement, inflation-adjusted.
  const futureMonthly = monthlyExpense * Math.pow(1 + inflation / 100, yearsToRetire);
  // Corpus needed = 25x annual expenses (4% safe withdrawal rule).
  const corpusNeeded = futureMonthly * 12 * 25;

  // Monthly SIP needed to reach that corpus.
  const i = returns / 100 / 12;
  const n = yearsToRetire * 12;
  const sip = n > 0 && i > 0
    ? corpusNeeded / (((Math.pow(1 + i, n) - 1) / i) * (1 + i))
    : 0;

  return (
    <CalcShell
      meta={meta}
      note={
        <span>
          <strong>The 25x rule:</strong> A corpus of about 25× your annual expenses
          can sustain a 4% yearly withdrawal indefinitely. Start early time is
          your biggest ally.
        </span>
      }
    >
      <Row>
        <Col md={6}>
          <CalcField label="Current age" value={currentAge} onChange={setCurrentAge} min={18} max={59} suffix="yr" />
          <CalcField label="Retirement age" value={retireAge} onChange={setRetireAge} min={40} max={70} suffix="yr" />
          <CalcField label="Current monthly expense (₹)" value={monthlyExpense} onChange={setMonthlyExpense} min={5000} max={500000} step={1000} prefix="₹" />
          <CalcField label="Inflation (% p.a.)" value={inflation} onChange={setInflation} min={2} max={10} step={0.5} suffix="%" />
          <CalcField label="Expected return (% p.a.)" value={returns} onChange={setReturns} min={4} max={20} step={0.5} suffix="%" />
        </Col>
        <Col md={6}>
          <div className="fi-calc-result mb-3">
            <div>Corpus you'll need</div>
            <div className="big">{formatINR(corpusNeeded)}</div>
          </div>
          <Row className="g-3">
            <Col xs={6}>
              <div className="fi-stat">
                <div className="num">{formatINR(sip)}</div>
                <small className="text-muted">Monthly SIP needed</small>
              </div>
            </Col>
            <Col xs={6}>
              <div className="fi-stat">
                <div className="num">{formatINR(futureMonthly)}</div>
                <small className="text-muted">Monthly expense at {retireAge}</small>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </CalcShell>
  );
}

export default RetirementCalculator;
